import React from 'react';
import { Wrench, Phone, Mail, Calendar } from 'lucide-react';
import { useSiteSettings } from '../contexts/SiteSettingsContext';

/**
 * Branded navy Maintenance Mode page. Rendered instead of the actual site
 * for public visitors when maintenance.enabled is true. Admins bypass via
 * ?preview=1 or by being at /admin (never blocked).
 *
 * Emits proper meta tags so search engines don't index the placeholder.
 */
const MaintenancePage = ({ maintenance, business }) => {
  const heading = maintenance?.heading || "We'll be right back";
  const message = maintenance?.message || "TayLord Arms is performing brief maintenance. Please check back shortly.";
  const showPhone = maintenance?.show_phone !== false;
  const showEmail = maintenance?.show_email !== false;
  const reopening = maintenance?.reopening_date;

  return (
    <>
      <meta name="robots" content="noindex,nofollow" />
      <section
        data-testid="maintenance-page"
        className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4 py-12"
      >
        <div className="max-w-xl text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-red-950/40 border border-red-900/60 mb-6">
            <Wrench className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold mb-4 tracking-tight">{heading}</h1>
          <p className="text-gray-300 text-lg leading-relaxed mb-8">{message}</p>

          {reopening && (
            <div className="inline-flex items-center gap-2 rounded-md bg-amber-950/40 border border-amber-800/60 px-4 py-2 mb-8" data-testid="maintenance-reopening">
              <Calendar className="w-4 h-4 text-amber-400" />
              <span className="text-amber-200 text-sm">Back on <strong>{reopening}</strong></span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-md mx-auto">
            {showPhone && business?.phone && (
              <a href={`tel:${business.phone}`} data-testid="maintenance-phone" className="flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-md px-4 py-3 transition-colors">
                <Phone className="w-4 h-4 text-red-400" />
                <span className="text-white text-sm font-semibold">{business.phone}</span>
              </a>
            )}
            {showEmail && business?.email && (
              <a href={`mailto:${business.email}`} data-testid="maintenance-email" className="flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 border border-slate-800 rounded-md px-4 py-3 transition-colors">
                <Mail className="w-4 h-4 text-red-400" />
                <span className="text-white text-sm truncate">{business.email}</span>
              </a>
            )}
          </div>

          <p className="text-gray-500 text-xs mt-10">{business?.name || 'TayLord Arms LLC'}</p>
        </div>
      </section>
    </>
  );
};

const MaintenanceGate = () => {
  const { maintenance, business } = useSiteSettings();
  if (!maintenance?.enabled) return null;
  return <MaintenancePage maintenance={maintenance} business={business} />;
};

export default MaintenanceGate;
